import { useState } from 'react';
import type { Recommendation } from '../types';
import { insights } from '../data';
import { Arrow, Branch, Bulb, Chart, Layers, LinkIcon, Plus, Refresh, Sparkle, Warn } from './DocIcons';

// ── Recommendation cards ───────────────────────────────────────────────────

const REC_STYLE: Record<string, { icon: React.FC<{ size?: number }>; tone: string; soft: string; ink: string; line: string; label: string }> = {
  improve:   { icon: Bulb,  tone: 'var(--blue)',                   soft: 'var(--blue-soft)',  ink: 'var(--blue-ink)',  line: 'var(--blue-line)',   label: 'Improve'   },
  conflict:  { icon: Warn,  tone: 'oklch(0.62 0.16 35)',           soft: 'oklch(0.96 0.04 40)', ink: 'oklch(0.5 0.16 35)', line: 'oklch(0.86 0.07 40)', label: 'Conflict' },
  gap:       { icon: Plus,  tone: 'var(--ai)',                     soft: 'var(--ai-soft)',    ink: 'var(--ai-ink)',    line: 'var(--ai-line)',     label: 'Gap'       },
  visualize: { icon: Chart, tone: 'var(--good)',                   soft: 'var(--good-soft)',  ink: 'oklch(0.45 0.12 155)', line: 'oklch(0.84 0.07 155)', label: 'Visualize' },
};

function RecCard({ rec, onAction, onJump }: { rec: Recommendation; onAction: (r: Recommendation) => void; onJump: (id: string) => void }) {
  const s = REC_STYLE[rec.kind] ?? REC_STYLE.improve;
  const [busy, setBusy] = useState(false);
  const Icon = s.icon;

  return (
    <div style={{
      border: '1px solid var(--line)', borderRadius: 'var(--r)', background: 'var(--surface)',
      padding: 12, animation: 'doc-fade-up 0.3s ease', boxShadow: 'var(--shadow-sm)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 7 }}>
        <span style={{ width: 22, height: 22, borderRadius: 6, background: s.soft, color: s.ink, display: 'grid', placeItems: 'center', border: `1px solid ${s.line}` }}>
          <Icon size={13} />
        </span>
        <span className="doc-chip" style={{ background: s.soft, color: s.ink, border: `1px solid ${s.line}` }}>{s.label}</span>
        <button onClick={() => onJump(rec.section)} className="mono" style={{
          marginLeft: 'auto', fontSize: 9.5, color: 'var(--muted)', border: 'none',
          background: 'transparent', display: 'flex', alignItems: 'center', gap: 3, cursor: 'pointer', fontFamily: 'var(--mono)',
        }}>
          jump <Arrow size={11} />
        </button>
      </div>
      <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4, color: 'var(--ink)' }}>{rec.title}</div>
      <div style={{ fontSize: 12.5, color: 'var(--ink-2)', lineHeight: 1.5 }}>{rec.body}</div>
      <button
        onClick={() => { setBusy(true); onAction(rec); setTimeout(() => setBusy(false), 1600); }}
        style={{
          marginTop: 10, display: 'flex', alignItems: 'center', gap: 6, height: 28, padding: '0 10px',
          border: `1px solid ${s.line}`, background: s.soft, color: s.ink, borderRadius: 'var(--r-sm)',
          fontWeight: 600, fontSize: 12, cursor: 'pointer', fontFamily: 'var(--sans)',
        }}
      >
        {busy ? <Refresh size={13} className="doc-spin" /> : <Icon size={13} />}
        {rec.action}
      </button>
    </div>
  );
}

function RecTab({ recs, onAction, onJump }: { recs: Recommendation[]; onAction: (r: Recommendation) => void; onJump: (id: string) => void }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>{recs.length} suggestions for this document</span>
        <span style={{ marginLeft: 'auto', width: 7, height: 7, borderRadius: 4, background: 'var(--ai)' }} className="doc-pulse" />
        <span className="mono" style={{ fontSize: 10, color: 'var(--ai-ink)' }}>live</span>
      </div>
      {recs.map((r) => <RecCard key={r.id} rec={r} onAction={onAction} onJump={onJump} />)}
    </div>
  );
}

// ── Insights tab ───────────────────────────────────────────────────────────

function SubHead({ icon: Icon, title }: { icon: React.FC<{ size?: number }>; title: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7, color: 'var(--ink-2)' }}>
      <Icon size={14} /><span style={{ fontWeight: 700, fontSize: 12.5 }}>{title}</span>
    </div>
  );
}

function RelGraph() {
  const nodes = [
    { x: 130, y: 78, label: 'PRD-031', center: true },
    { x: 36,  y: 30, label: 'BC-007',  strong: true },
    { x: 224, y: 30, label: 'FSD-022', strong: true },
    { x: 36,  y: 126, label: 'MR-014' },
    { x: 224, y: 126, label: 'ADR-009' },
  ];
  const c = nodes[0];
  return (
    <svg viewBox="0 0 260 156" style={{ width: '100%', height: 'auto', display: 'block' }}>
      {nodes.slice(1).map((n, i) => (
        <line key={i} x1={c.x} y1={c.y} x2={n.x} y2={n.y}
          stroke={n.strong ? 'var(--blue-line)' : 'var(--line)'}
          strokeWidth={n.strong ? 2 : 1.3}
          strokeDasharray={n.strong ? undefined : '3 3'} />
      ))}
      {nodes.map((n, i) => (
        <g key={i}>
          <rect x={n.x - 30} y={n.y - 11} width="60" height="22" rx="6"
            fill={n.center ? 'var(--ink)' : 'var(--surface)'}
            stroke={n.center ? 'var(--ink)' : 'var(--line)'} strokeWidth="1" />
          <text x={n.x} y={n.y + 3.5} textAnchor="middle" fontFamily="var(--mono)" fontSize="9.5" fontWeight="600"
            fill={n.center ? 'white' : 'var(--ink-2)'}>{n.label}</text>
        </g>
      ))}
    </svg>
  );
}

function InsightTab() {
  const I = insights;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, padding: 14 }}>
      {/* relationships */}
      <div>
        <SubHead icon={Branch} title="Document relationships" />
        <div style={{ border: '1px solid var(--line)', borderRadius: 'var(--r)', background: 'var(--surface)', padding: '14px 12px', marginTop: 8 }}>
          <RelGraph />
        </div>
      </div>

      {/* change impact */}
      <div>
        <SubHead icon={Warn} title="Change impact" />
        <div style={{ marginTop: 8, border: '1px solid oklch(0.86 0.07 40)', borderRadius: 'var(--r)', overflow: 'hidden' }}>
          <div style={{ padding: '9px 12px', background: 'oklch(0.96 0.04 40)', color: 'oklch(0.45 0.16 35)', fontWeight: 700, fontSize: 12.5, display: 'flex', alignItems: 'center', gap: 7 }}>
            <Warn size={14} />{I.impact.title}
          </div>
          <div style={{ background: 'var(--surface)' }}>
            {I.impact.affected.map((a, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderTop: '1px solid var(--line-2)' }}>
                <span className="mono" style={{ fontSize: 11, fontWeight: 600, color: 'var(--blue-ink)', width: 58 }}>{a.doc}</span>
                <span style={{ fontSize: 12, color: 'var(--ink-2)', flex: 1, lineHeight: 1.4 }}>{a.detail}</span>
                <span className="doc-chip" style={{
                  background: a.level === 'high' ? 'oklch(0.95 0.05 40)' : 'var(--bg-2)',
                  color: a.level === 'high' ? 'oklch(0.5 0.16 35)' : 'var(--muted)',
                }}>{a.level}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* shared content */}
      <div>
        <SubHead icon={Layers} title="Shared content" />
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {I.shared.map((s, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '8px 11px', border: '1px solid var(--line)', borderRadius: 'var(--r-sm)', background: 'var(--surface)' }}>
              <LinkIcon size={14} style={{ color: 'var(--faint)' }} />
              <span style={{ fontSize: 12.5, fontWeight: 600, flex: 1 }}>{s.name}</span>
              <div style={{ display: 'flex', gap: 3 }}>
                {s.docs.map((d, j) => (
                  <span key={j} className="mono" style={{ fontSize: 9, fontWeight: 600, background: 'var(--blue-soft)', color: 'var(--blue-ink)', padding: '2px 5px', borderRadius: 4 }}>{d}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Generate tab ───────────────────────────────────────────────────────────

function Pick({ value, set, opts }: { value: string; set: (v: string) => void; opts: string[] }) {
  return (
    <div style={{ position: 'relative', flex: 1 }}>
      <select
        value={value} onChange={(e) => set(e.target.value)}
        style={{
          width: '100%', appearance: 'none', border: '1px solid var(--line)', borderRadius: 'var(--r-sm)',
          background: 'var(--surface)', padding: '5px 24px 5px 9px', fontFamily: 'var(--sans)',
          fontSize: 11.5, fontWeight: 600, color: 'var(--ink-2)', cursor: 'pointer',
        }}
      >
        {opts.map((o) => <option key={o}>{o}</option>)}
      </select>
    </div>
  );
}

function GenerateTab({ activeSectionTitle, onGenSection, onGenDoc, generating }: {
  activeSectionTitle: string | null; onGenSection: () => void; onGenDoc: () => void; generating: boolean;
}) {
  const [prompt, setPrompt] = useState('');
  const [model, setModel] = useState('Vibelin Pro');
  const [tone, setTone] = useState('Concise');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: 14 }}>
      <SubHead icon={Sparkle} title="Generate content" />
      <div style={{ border: '1px solid var(--ai-line)', borderRadius: 'var(--r)', overflow: 'hidden', background: 'var(--surface)' }}>
        <textarea
          value={prompt} onChange={(e) => setPrompt(e.target.value)}
          placeholder={`Describe what to generate${activeSectionTitle ? ` for "${activeSectionTitle}"` : ''}, or refine the section's prompt template…`}
          style={{
            width: '100%', border: 'none', resize: 'none', padding: '11px 12px', fontFamily: 'var(--sans)',
            fontSize: 12.5, color: 'var(--ink)', minHeight: 78, outline: 'none', background: 'transparent',
          }}
        />
        <div style={{ display: 'flex', gap: 6, padding: '8px 10px', borderTop: '1px solid var(--line-2)', background: 'var(--surface-2)' }}>
          <Pick value={model} set={setModel} opts={['Vibelin Pro', 'Vibelin Fast', 'Reasoning']} />
          <Pick value={tone} set={setTone} opts={['Concise', 'Detailed', 'Formal']} />
        </div>
      </div>

      <button className="doc-btn ai" onClick={onGenSection} disabled={generating} style={{ height: 38, justifyContent: 'center' }}>
        {generating ? <Refresh size={16} className="doc-spin" /> : <Sparkle size={16} />}
        {generating ? 'Generating…' : activeSectionTitle ? `Generate "${activeSectionTitle}"` : 'Generate selected section'}
      </button>
      <button className="doc-btn" onClick={onGenDoc} disabled={generating} style={{ height: 36, justifyContent: 'center' }}>
        <Layers size={15} />Generate all empty sections
      </button>

      <div style={{ marginTop: 4, padding: 12, background: 'var(--ai-soft)', borderRadius: 'var(--r)', border: '1px solid var(--ai-line)' }}>
        <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--ai-ink)', marginBottom: 5, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Bulb size={13} />Context used
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--ink-2)', lineHeight: 1.5 }}>
          Vibelin grounds generation in this project's Market Research, Business Case, and any filled sections of the current document.
        </div>
      </div>
    </div>
  );
}

// ── Main AI panel ──────────────────────────────────────────────────────────

interface AIPanelProps {
  recs: Recommendation[];
  onAction: (r: Recommendation) => void;
  onJump: (id: string) => void;
  activeSectionTitle: string | null;
  onGenSection: () => void;
  onGenDoc: () => void;
  generating: boolean;
}

type Tab = 'recs' | 'insights' | 'generate';

export function DocAIPanel({ recs, onAction, onJump, activeSectionTitle, onGenSection, onGenDoc, generating }: AIPanelProps) {
  const [tab, setTab] = useState<Tab>('recs');
  const tabs: [Tab, string][] = [['recs', 'Suggestions'], ['insights', 'Insights'], ['generate', 'Generate']];

  return (
    <aside style={{ borderLeft: '1px solid var(--line)', background: 'var(--bg)', display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '13px 14px 0' }}>
        <span style={{ width: 24, height: 24, borderRadius: 7, background: 'var(--ai)', color: 'white', display: 'grid', placeItems: 'center' }}>
          <Sparkle size={14} />
        </span>
        <span style={{ fontWeight: 800, fontSize: 14 }}>AI Assistant</span>
      </div>

      <div style={{ display: 'flex', gap: 2, padding: '10px 12px 0', borderBottom: '1px solid var(--line)' }}>
        {tabs.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} style={{
            position: 'relative', border: 'none', background: 'transparent',
            padding: '7px 10px 11px', fontWeight: 600, fontSize: 12.5, cursor: 'pointer',
            color: tab === k ? 'var(--ink)' : 'var(--muted)', fontFamily: 'var(--sans)',
          }}>
            {label}
            {tab === k && (
              <span style={{ position: 'absolute', left: 6, right: 6, bottom: -1, height: 2, borderRadius: 2, background: 'var(--ai)' }} />
            )}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, overflowY: 'auto' }}>
        {tab === 'recs'      && <RecTab recs={recs} onAction={onAction} onJump={onJump} />}
        {tab === 'insights'  && <InsightTab />}
        {tab === 'generate'  && <GenerateTab activeSectionTitle={activeSectionTitle} onGenSection={onGenSection} onGenDoc={onGenDoc} generating={generating} />}
      </div>
    </aside>
  );
}

